/**
 * IdentityLandingPageV2 — Phase 6 (P0-IDENT).
 *
 * Landing page for the Identity plane. Identity is a top-level operational
 * surface under Operate (alongside Workflows and Approvals), not a tab
 * buried in Settings. The premise: identity is something you RUN, the
 * same way you run workflows — SPIFFE/SVID lifecycle, federation, trust
 * bundle exchange, rotation health.
 *
 * Layout
 *   1. Fleet KPI strip — live state of the identity plane across the org
 *   2. Three scope cards — Organization / Tenant / Agent
 *      Each is a click-through into its detail page; the body of each
 *      card lists the configuration surfaces it owns so users can preview
 *      what they'll find inside.
 *   3. Operational views shelf — Federation Map, SPIRE Infrastructure,
 *      Rotation Health, Compliance Evidence (links to follow-up phases)
 *
 * Inheritance model (locked in by user, 2026-05):
 *   Every setting starts as INHERITED from its parent scope. Any scope
 *   can opt to override per-setting. Org sets the global default; Tenant
 *   can override; Agent can override the (Org/Tenant) resolved value.
 *   The Tenant inheritance pane (sibling page) visualises which tenants
 *   inherit vs override across the org's settings.
 *
 * Data: this page renders against IdentityScopeService stub data from
 * GET /api/v1/identity/organization until persistence lands in 6G+.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Bot, Fingerprint, ShieldCheck,
  Network, Server, Activity, FileCheck2, Plus, AlertTriangle, CheckCircle2, Ban, Clock, Plug,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { MetricCard } from '../components/v2/MetricCard';
import { getIdentityFleet } from '../services/api';

/* ────────────────────────────────────────────────────────────────
   Page data — stub until IdentityScopeController lands.
   Shape mirrors what /api/v1/identity/organization will return.
   ──────────────────────────────────────────────────────────────── */

/* Identity redesign ID-0/ID-1 — the Fleet KPI strip now renders HONEST
 * per-lifecycle-state counts from GET /api/v1/identity/fleet (backed by
 * agents.identity_state, V266). This replaces the fabricated fixtures
 * ("247 active SVIDs / 99.97% rotation / 4-4 SPIRE health") that
 * contradicted the preview / NoOp-SPIRE reality. Each card maps to a
 * lifecycle state; '__total__' is the live (non-deleted) agent total. */
const FLEET_CARDS = [
  { key: '__total__',        label: 'Total agents',     tone: 'action',  icon: <Bot size={14} /> },
  { key: 'IDENTITY_ASSIGNED', label: 'Identity assigned', tone: 'warning', icon: <Fingerprint size={14} /> },
  { key: 'ACTIVE',           label: 'Active',           tone: 'trust',   icon: <ShieldCheck size={14} /> },
  { key: 'VERIFIED',         label: 'Verified',         tone: 'success', icon: <CheckCircle2 size={14} /> },
  { key: 'REVOKED',          label: 'Revoked',          tone: 'danger',  icon: <Ban size={14} /> },
  { key: 'EXPIRED',          label: 'Expired',          tone: 'muted',   icon: <Clock size={14} /> },
];

const SCOPES = [
  {
    id: 'organization',
    label: 'Organization',
    subtitle: 'AcmeBank Inc. · inherited by 8 tenants · 247 agents',
    headline: 'Trust domains · SPIRE',
    icon: Building2,
    tone: 'action',
    summary: '3 trust domains · 4 SPIRE nodes · root CA expires in 847 d',
    sections: [
      { label: 'Trust domains',         value: '3 (2 federated, 1 internal)' },
      { label: 'Federation',            value: '2 active partners, 1 bundle expired' },
      { label: 'SPIRE bootstrap',       value: 'spire-east + spire-west · HA' },
      { label: 'Root CA & trust bundle',value: 'AcmeBank-Root-2026 (ECDSA P-384)' },
      { label: 'Rotation defaults',     value: '1h SVID · ⅔ TTL window' },
      { label: 'Compliance bindings',   value: 'SOX · HIPAA · Probation watch' },
    ],
    cta: { label: 'Open Organization', path: '/identity/organization' },
  },
  {
    id: 'tenant',
    label: 'Tenant',
    subtitle: '8 tenants configured · 6 inherit Org · 2 override',
    headline: '8 tenants · overrides',
    icon: Users,
    tone: 'trust',
    summary: 'Capital Markets and Fraud & AML carry per-tenant overrides',
    sections: [
      { label: 'Trust-domain override',      value: '1 tenant uses dedicated domain' },
      { label: 'Dedicated SPIRE',            value: '1 tenant on isolated cluster' },
      { label: 'Registration template',      value: '3 templates · 8 tenants bound' },
      { label: 'Rotation policy override',   value: '1 tenant (Capital Markets: 15 min TTL)' },
      { label: 'Tenant federation',          value: 'Cross-tenant trust disabled by default' },
      { label: 'Audit destinations',         value: 'All routing to org-wide pipeline' },
    ],
    cta: { label: 'Open Tenants', path: '/identity/tenants' },
  },
  {
    id: 'agent',
    label: 'Agent',
    subtitle: '247 agents with SVIDs · 12 minting children · 3 awaiting registration',
    headline: '247 SVIDs',
    icon: Bot,
    tone: 'cyan',
    summary: '3 in PROBATION trust band · 8 SVIDs expire within 24 h',
    sections: [
      { label: 'SVID lifecycle',         value: 'Serial, expiry, Rotate-now control' },
      { label: 'Registration entry',     value: 'Per-agent SPIRE registration record' },
      { label: 'Rotation override',      value: 'Agent-level TTL override (rare)' },
      { label: 'Child SVID policy',      value: 'Delegation, minting allowlist' },
      { label: 'X.509 claims',           value: 'Tenant, role, trust band, attestation' },
      { label: 'Lifecycle events',       value: 'Rotation, revocation, federation join' },
    ],
    /* Agent-scope list + detail shipped in Phase 6G. The list page at
     * /identity/agents shows every agent's identity-plane state with
     * SVID lifecycle classification; row clicks deep-link into the
     * 7-section detail at /identity/agents/:id. */
    cta: { label: 'Open Agents', path: '/identity/agents' },
  },
];

const OPERATIONAL_VIEWS = [
  {
    id: 'federation-map',
    label: 'Federation map',
    description: 'Trust-domain graph with bundle freshness per edge',
    icon: Network,
    path: '/identity/federation-map',
    // Enabled by Stage 4-4.
  },
  {
    id: 'spire-health',
    label: 'SPIRE infrastructure',
    description: 'Server topology, trust domains, root CA, rotation, federation',
    icon: Server,
    path: '/identity/spire-health',
  },
  {
    id: 'rotation-health',
    label: 'Rotation health',
    description: '24 h rotation heatmap and near-expiry watchlist',
    icon: Activity,
    path: '/identity/rotation-health',
    comingSoon: true,  // remains deferred
  },
  {
    id: 'compliance-evidence',
    label: 'Compliance evidence',
    description: 'Per-framework binding coverage by agent',
    icon: FileCheck2,
    path: '/identity/compliance-evidence',
    // Enabled by Stage 4-5.
  },
  {
    id: 'connected-apps',
    label: 'Connected applications',
    description: 'Applications allowed to sign your people in to the protected surface',
    icon: Plug,
    path: '/identity/connected-apps',
  },
  {
    id: 'sso',
    label: 'Single sign-on',
    description: 'External identity providers your people sign in through',
    icon: Fingerprint,
    path: '/identity/sso',
    accent: true,
    badge: 'New',
  },
];

/* The two federation-and-access surfaces are the complementary halves of federation:
   Connected applications = apps that sign in USING Prove7; Single sign-on = external providers
   Prove7 federates users FROM. They render together, apart from the read-mostly operational views. */
const FEDERATION_VIEW_IDS = new Set(['federation-map', 'spire-health', 'connected-apps', 'sso']);

/* ────────────────────────────────────────────────────────────────
   ScopeCard — single scope row.
   ──────────────────────────────────────────────────────────────── */

/* Static tone-class maps. Tailwind's JIT compiler needs full class names
 * present in the source — dynamic concatenation like `bg-v2-${tone}-soft`
 * would yield strings the compiler can't see, and the classes would be
 * absent from the production bundle. Same pattern as MetricCard. */
const RAIL_BG = {
  action: 'bg-v2-action-soft',
  trust:  'bg-v2-trust-soft',
  cyan:   'bg-v2-cyan-soft',
};
const RAIL_ICON_TEXT = {
  action: 'text-v2-action',
  trust:  'text-v2-trust',
  cyan:   'text-v2-cyan',
};

function ScopeCard({ scope }) {
  const navigate = useNavigate();
  const Icon = scope.icon;

  // Compact scope tile (mockup-aligned): icon + label + one-line headline; the full per-scope
  // configuration lives on the scope detail page reached by clicking through.
  return (
    <Card padding="md" interactive onClick={() => navigate(scope.cta.path)}>
      <div className="flex items-start gap-3">
        <div className={'w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 '
          + (RAIL_BG[scope.tone] || 'bg-v2-muted') + ' ' + (RAIL_ICON_TEXT[scope.tone] || 'text-v2-ink')}>
          <Icon size={18} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-v2-ink">{scope.label}</div>
          <div className="text-[11.5px] text-v2-ink-secondary mt-0.5 truncate">{scope.headline}</div>
        </div>
      </div>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   OperationalViewTile — small tile for the bottom shelf.
   ──────────────────────────────────────────────────────────────── */

function OperationalViewTile({ view }) {
  const navigate = useNavigate();
  const Icon = view.icon;
  return (
    <Card
      padding="md"
      interactive={!view.comingSoon}
      onClick={view.comingSoon ? undefined : () => navigate(view.path)}
      className={[
        view.comingSoon ? 'opacity-70' : '',
        view.accent ? 'border-2 border-v2-accent' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="flex items-start gap-3">
        <div className={'w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 '
          + (view.accent ? 'bg-v2-accent-soft text-v2-accent' : 'bg-v2-muted text-v2-ink-secondary')}>
          <Icon size={16} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[13.5px] font-semibold text-v2-ink">{view.label}</div>
            {view.comingSoon && <Pill tone="muted" size="sm">Soon</Pill>}
            {view.badge && <Pill tone="cyan" size="sm">{view.badge}</Pill>}
          </div>
          <div className="text-[11.5px] text-v2-ink-secondary mt-0.5">{view.description}</div>
        </div>
      </div>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   IdentityLandingPageV2 — top-level export
   ──────────────────────────────────────────────────────────────── */

export default function IdentityLandingPageV2() {
  const navigate = useNavigate();

  // ID-0/ID-1 — honest Fleet state. preview = NoOp SPIRE / no SVID minted.
  const [fleet, setFleet] = useState(null);
  useEffect(() => {
    let alive = true;
    getIdentityFleet()
      // apiFetch resolves { ok, data, etag } — unwrap .data for the fleet payload.
      .then((res) => { if (alive) setFleet((res && res.data) ? res.data : { mode: 'preview', total: 0, byState: {} }); })
      .catch(() => { if (alive) setFleet({ mode: 'preview', total: 0, byState: {} }); });
    return () => { alive = false; };
  }, []);
  const mode = fleet?.mode || 'preview';
  const byState = fleet?.byState || {};
  const cardValue = (key) => key === '__total__' ? (fleet?.total ?? 0) : (byState[key] ?? 0);

  return (
    <V2PageShell
      title="Identity"
      subtitle="SPIFFE / SVID lifecycle, federation, and trust-domain configuration across Org, Tenant, and Agent scopes."
      tags={
        <>
          <Pill tone="trust" size="sm" dot>SPIFFE plane</Pill>
          <Pill tone="action" size="sm">P0-IDENT</Pill>
          {/* ID-0/ID-1 — the Fleet KPI strip below is now REAL (GET
              /identity/fleet → agents.identity_state). The mode pill tells
              the truth about the deployment: "Preview" under NoOp SPIRE
              (no SVID minted), "Live" once a real issuer is wired. The
              three scope cards below remain config previews until per-scope
              persistence lands; they navigate to surfaces that query real
              APIs. */}
          <Pill tone={mode === 'wired' ? 'success' : 'warning'} size="sm">
            {mode === 'wired' ? 'Live' : 'Preview'}
          </Pill>
        </>
      }
      actions={
        <>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<ShieldCheck size={14} />}
            onClick={() => navigate('/native-trust')}
          >
            Trust Center
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Plus size={14} />}
            onClick={() => navigate('/identity/organization')}
          >
            New trust domain
          </Button>
        </>
      }
    >
      {/* Scope cards — compact 3-across (Organization / Tenant / Agent); full config on click ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-7">
        {SCOPES.map((s) => <ScopeCard key={s.id} scope={s} />)}
      </div>

      {/* Federation and access ─────────────────────────────────── */}
      <div className="mb-3">
        <div className="text-[14px] font-semibold text-v2-ink">Federation and access</div>
        <div className="text-[12px] text-v2-ink-secondary">
          Connected applications sign in using Prove7; Single sign-on federates your people in from
          external providers.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-7">
        {OPERATIONAL_VIEWS.filter((v) => FEDERATION_VIEW_IDS.has(v.id))
          .map((v) => <OperationalViewTile key={v.id} view={v} />)}
      </div>

      {/* Operational views shelf ───────────────────────────────── */}
      <div className="mb-3">
        <div className="text-[14px] font-semibold text-v2-ink">Operational views</div>
        <div className="text-[12px] text-v2-ink-secondary">Read-mostly surfaces for live identity-plane state</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-7">
        {OPERATIONAL_VIEWS.filter((v) => !FEDERATION_VIEW_IDS.has(v.id))
          .map((v) => <OperationalViewTile key={v.id} view={v} />)}
      </div>

      {/* Fleet — live SVID lifecycle counts + honesty banner (condensed, below the entry point) ── */}
      <div className="mb-3">
        <div className="text-[14px] font-semibold text-v2-ink">Fleet</div>
        <div className="text-[12px] text-v2-ink-secondary">Live SVID lifecycle counts across this scope</div>
      </div>
      <div
        className={
          'flex items-start gap-2.5 rounded-v2-md px-3.5 py-2.5 mb-3 ' +
          (mode === 'wired' ? 'bg-v2-action-soft' : 'bg-v2-warning-soft')
        }
      >
        {mode === 'wired'
          ? <CheckCircle2 size={15} className="text-v2-action mt-0.5 flex-shrink-0" aria-hidden="true" />
          : <AlertTriangle size={15} className="text-v2-warning mt-0.5 flex-shrink-0" aria-hidden="true" />}
        <div className="text-[12px] text-v2-ink leading-relaxed">
          {mode === 'wired' ? (
            <><span className="font-semibold">SPIRE wired.</span> Real X.509-SVIDs are minted and verified at the gateway edge. Counts below are live.</>
          ) : (
            <><span className="font-semibold">Preview · BYO-SPIRE.</span> SPIRE Workload API not wired — 0 SVIDs minted. Counts below reflect real lifecycle state, not demo data.</>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {FLEET_CARDS.map((c) => (
          <MetricCard
            key={c.key}
            label={c.label}
            value={String(cardValue(c.key))}
            icon={c.icon}
            tone={c.tone}
          />
        ))}
      </div>
    </V2PageShell>
  );
}
